export type PackagePeriod = "monthly" | "quarterly" | "halfyearly" | "yearly";

export interface PackageRate {
  period: string;
  rates: number;
  discountRate?: number | null;
}

export const PERIOD_LABELS: Record<string, string> = {
  monthly: "Monthly",
  quarterly: "Quarterly",
  halfyearly: "Half Yearly",
  yearly: "Yearly",
};

export const PERIOD_SUFFIXES: Record<string, string> = {
  monthly: "/mo",
  quarterly: "/qtr",
  halfyearly: "/6mo",
  yearly: "/yr",
};

export const getPeriodLabel = (period?: string) => {
  if (!period) return "";
  return PERIOD_LABELS[period] ?? period;
};

export const getPeriodSuffix = (period?: string) => {
  if (!period) return "/mo";
  return PERIOD_SUFFIXES[period] ?? "";
};

export const getPackagePeriods = (pack: any): PackageRate[] => {
  if (Array.isArray(pack?.packageRates) && pack.packageRates.length > 0) {
    return pack.packageRates.filter(
      (rate: PackageRate) => !!rate?.period && rate?.rates != null,
    );
  }

  if (pack?.totalRates != null) {
    return [
      {
        period: "monthly",
        rates: Number(pack.totalRates),
        discountRate: Number(pack?.discountRate ?? 0),
      },
    ];
  }

  return [];
};

export const getDefaultPeriod = (pack: any): string => {
  return getPackagePeriods(pack)[0]?.period ?? "";
};

export const getPackageRateForPeriod = (
  pack: any,
  period?: string,
): PackageRate | undefined => {
  const periods = getPackagePeriods(pack);
  if (!periods?.length) return undefined;

  if (period) {
    return periods.find((item) => item?.period === period) ?? periods[0];
  }

  return periods[0];
};

export const getPackageBaseRate = (pack: any, period?: string): number => {
  const match = getPackageRateForPeriod(pack, period);

  if (match) {
    return Number(match?.rates ?? 0);
  }

  return Number(pack?.totalRates ?? 0);
};

export const getPackageDiscountRate = (pack: any, period?: string): number => {
  const match = getPackageRateForPeriod(pack, period);

  if (match && match.discountRate != null) {
    return Number(match.discountRate ?? 0);
  }

  return Number(pack?.discountRate ?? 0);
};

export const getPackagePrice = (pack: any, period?: string): number => {
  const discount = getPackageDiscountRate(pack, period);

  if (discount > 0) {
    return discount;
  }

  return getPackageBaseRate(pack, period);
};

export const hasPackageDiscount = (pack: any, period?: string): boolean => {
  return getPackageDiscountRate(pack, period) > 0;
};

export const getPackageTrialDays = (pack: any): number => {
  const days = Number(pack?.trialdays ?? 0);
  return Number.isFinite(days) ? days : 0;
};

export const hasPackageFreeTrial = (pack: any): boolean => {
  return !!pack?.isFreeMonth && getPackageTrialDays(pack) > 0;
};
